import { Component, ElementRef, Inject, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TemplateMessageObject } from '@trendbuild/trend-cloud-api';
import { AbstractComponent, AlertModalComponent, Attendance, AttendanceService, AttendanceStatusEnum, Channel, ChannelService, ChannelTypeEnum, Contact, ContactService, CreateAttendanceFromTemplate, Department, DepartmentService, isRCSConversational, MessageHelper, MetadataMessage, Team, TeamService, User, waTemplateParamValues } from 'lib-trend-core';
import { forkJoin, of, takeUntil } from 'rxjs';
import { getAttendance } from '../../state/actions';
import { AppState } from '../../state/app.state';

@Component({
    selector: 'new-conversation',
    // templateUrl: './new-conversation.component.html',
    templateUrl: './new-conversation.v2.component.html',
    styleUrl: './new-conversation.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactNewConversationComponent extends AbstractComponent implements OnInit {
  @ViewChild('btnSendTemplate') btnSendTemplate: ElementRef<HTMLButtonElement>;
  @ViewChild('modalContainer') modalContainer: ElementRef;

  step = 1;
  contacts = new Array<any>();
  selectedContact: Contact = null;
  errorMessage = "";

  channels: Array<Channel>;
  selectedChannel: Channel;

  selectedTemplate: TemplateMessageObject = undefined;
  templateMessage: MetadataMessage = {
    headerParams: [],
    bodyParams: [],
    previewText: ''
  };

  execMessage = null;

  listTeam: Array<Team>;
  listDepartments: Array<Department>;

  selectedDepartment: Department;

  variables: Record<string, string> = {};

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ContactNewConversationComponent>,
    private store: Store<AppState>,
    private contactService: ContactService,
    private channelService: ChannelService,
    private attendanceService: AttendanceService,
    private dialog: MatDialog,
    private teamService: TeamService,
    private departmentService: DepartmentService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super(injector);
  }

  ngOnInit() {
    if (this.data?.contact) {
      this.selectedContact = this.data.contact;
      this.loadInitialData();
    } else if (this.data?.contactId) {
      this.contactService.getById(this.data.contactId).subscribe({
        next: (contact: Contact) => {
          this.selectedContact = contact;
          this.loadInitialData();
        },
        error: (err) => this.alertService.error(err.error.message)
      });
    } else {
      this.loadInitialData();
    }
  }

  loadInitialData() {
    // Attendants and supervisors only see the channels of their own departments — the same
    // departments `getListDepartments` offers them once a channel is picked.
    const idDepartmentsUser = this.getIDDepartmentsUserLogged() ?? [];
    const restrictToOwnDepartments = this.isAttendant() || this.isSupervisor();
    forkJoin({
      channels: this.channelService.getList(),
      departments: restrictToOwnDepartments ? this.departmentService.getList() : of(new Array<Department>()),
    }).pipe(takeUntil(this.destroy$)).subscribe(({ channels, departments }) => {
      const idChannelsUser = departments
        .filter((department: Department) => idDepartmentsUser.includes(department._id))
        // `/department/list` sends the channel as its bare id, not populated.
        .map((department: Department) => (department.channel as Channel)?._id ?? String(department.channel));
      // A campaign-only RCS account has no conversational route, so a conversation
      // started on it could never be answered — it is kept out of the picker rather
      // than failing on the first message.
      this.channels = channels.filter(item => (!restrictToOwnDepartments || idChannelsUser.includes(item._id))
        && item.type !== ChannelTypeEnum.INSTAGRAM_API
        && item.type !== ChannelTypeEnum.MESSENGER_API
        && (item.type !== ChannelTypeEnum.RCS_API || isRCSConversational(item)));
      if (this.channels?.length === 1) {
        this.selectedChannel = this.channels[0];
        this.selectChannel(this.selectedChannel);
      }
    });
  }

  setTemplateMessage(templateMessage: MetadataMessage) {
    this.templateMessage = templateMessage;
  }

  onContactSelected(contact: any) {
    this.selectedContact = contact;
  }

  selectChannel(channel: Channel) {
    this.selectedChannel = channel;
    this.getListDepartments(channel);
  }

  selectDepartment(department: Department) {
    this.selectedDepartment = department;
  }

  onSelectTemplate(template: TemplateMessageObject) {
    this.step = 3;
    this.selectedTemplate = template;
    this.buildVariables();
  }

  clearValuesToBack(): void {
    this.selectedChannel = null;
    this.selectedContact = null;
    this.selectedDepartment = null;
    this.selectedTemplate = null;
  }

  goToBack(): void {
    this.selectedTemplate = null;
    this.step = 1;
  }

  onClose() {
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }

  isFieldsSelected(): boolean {
    return !!this.selectedChannel && !!this.selectedContact && !!this.selectedDepartment;
  }

  getNameButton(): string {
    if (this.selectedChannel?.type === ChannelTypeEnum.CLOUD_API) {
      return this.t('attendance.newConversation.next');
    } else {
      return this.t('attendance.newConversation.startAttendance');
    }
  }

  sendTemplateMessage() {
    this.loading = true;
    if (this.selectedChannel.type === ChannelTypeEnum.CLOUD_API) {
      if (!this.selectedTemplate) {
        this.alertService.info(this.t('attendance.newConversation.selectTemplateAlert'));
        this.loading = false;
        return;
      }
      this.btnSendTemplate.nativeElement.disabled = true;

      const headerComponent = this.selectedTemplate.components.find(comp => comp.type === 'HEADER') as any;
      const bodyComponent = this.selectedTemplate.components.find(comp => comp.type === 'BODY');

      if (headerComponent?.format && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format) && !this.templateMessage.headerParams?.[0]) {
        this.alertService.info(this.t('attendance.newConversation.insertRequiredFile'));
        this.loading = false;
        return;
      }

      if (bodyComponent && waTemplateParamValues(this.templateMessage.bodyParams).some(param => !param?.toString().trim())) {
        this.alertService.info(this.t('attendance.newConversation.fillBodyParams'));
        this.loading = false;
        return;
      }
    }

    const createAttendanceFromTemplate: CreateAttendanceFromTemplate = {
      idDepartment: this.selectedDepartment._id,
      idChannel: this.selectedChannel._id,
      idContact: this.selectedContact._id,
      template: this.selectedTemplate,
      headerParams: this.templateMessage.headerParams,
      bodyParams: this.templateMessage.bodyParams,
      previewText: this.templateMessage.previewText
    }

    let createdAttendance: Attendance;
    this.attendanceService.createFromTemplate(createAttendanceFromTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attendance: Attendance) => {
          createdAttendance = attendance;
          this.store.dispatch(getAttendance({ idAttendance: attendance._id }));
        },
        complete: () => {
          this.loading = false;
          this.dialogRef.close(createdAttendance);
        },
        error: (err) => {
          this.btnSendTemplate.nativeElement.disabled = false;
          this.loading = false;
          this.alertService.error(err.error.message);
        },
      });
  }

  removeContact() {
    this.selectedContact = null;
  }

  private getListTeams(): void {
    this.teamService.getList().subscribe({
      next: (list: Array<Team>) => {
        this.listTeam = list
          .filter((team: Team) => {
            const userId = this.getCurrentUserUser()._id;
            const isAgent = team.agents.some((agent: User) => agent._id === userId);
            const isSupervisor = team.supervisors.some((supervisor: User) => supervisor._id === userId);
            return isAgent || isSupervisor;
          })
          .filter((team: Team) => team.department && team.department.removed !== true)
          .filter((team: Team) => { team.department.channel?.toString() === this.selectedChannel._id });
      },
    });
  }

  private getListDepartments(channel: Channel): void {
    this.departmentService.getDepartmentsByChannel(channel._id).subscribe({
      next: (list: Array<Department>) => {
        if (this.isAttendant() || this.isSupervisor()) {
          const ids = this.getIDDepartmentsUserLogged();
          this.listDepartments = list.reduce((acc: Array<Department>, department: Department) => {
            if (ids.includes(department._id)) {
              acc.push(department);
            }
            return acc;
          }, []);
        } else {
          this.listDepartments = list;
        }

      },
      error: (err: Error) => this.alertService.error(err.message),
    });
  }

  private buildVariables(): void {
    this.variables = {
      [this.t('attendance.newConversation.variableGreeting')]: MessageHelper.getGreeting(),
      [this.t('attendance.newConversation.variableContactName')]: this.selectedContact.name,
      [this.t('attendance.newConversation.variableAgentName')]: this.getNameCurrentUser(),
      [this.t('attendance.newConversation.variableDepartment')]: this.selectedDepartment.name,
    };
  }

  initAttendance(): void {
    if (!this.isFieldsSelected()) {
      this.alertService.info(this.t('attendance.newConversation.selectChannelDepartmentContact'));
      return;
    }
    this.errorMessage = "";
    clearTimeout(this.execMessage);

    this.attendanceService.existsContactStatus(this.selectedContact._id, [AttendanceStatusEnum.PENDING,
    AttendanceStatusEnum.IN_PROGRESS, AttendanceStatusEnum.PAUSED, AttendanceStatusEnum.OVERTIME], this.selectedChannel._id).subscribe(alreadyExistsContact => {
      if (alreadyExistsContact) {
        this.dialog.open(AlertModalComponent, {
          data: {
            title: this.t('attendance.newConversation.attendanceExistsTitle', { contact: this.selectedContact.name, channel: this.selectedChannel.name }),
            message: this.t('attendance.newConversation.attendanceExistsMessage'),
          },
        });

        this.modalContainer.nativeElement.scrollTo({
          top: 0,
          behavior: 'smooth'
        });

        this.execMessage = setTimeout(() => {
          this.errorMessage = "";
        }, 6000);

        return;
      } else {
        if (this.selectedChannel.type === ChannelTypeEnum.CLOUD_API) {
          this.step = 2;
        } else {
          this.selectedTemplate = null;
          this.sendTemplateMessage();
        }
      }
    });
  }
}
